import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Calendar, Upload, DollarSign, Star, TrendingUp, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PhotographerDashboard = () => {
  const bookings = [
    {
      id: 1,
      client: 'John & Mary Smith',
      type: 'Wedding Photography',
      date: 'December 15, 2024',
      status: 'completed',
      amount: 4500,
      assetsUploaded: true,
    },
    {
      id: 2,
      client: 'TechCorp Inc.',
      type: 'Corporate Event',
      date: 'January 20, 2025',
      status: 'upcoming',
      amount: 2500,
      assetsUploaded: false,
    },
    {
      id: 3,
      client: 'Sarah Wilson',
      type: 'Portrait Session',
      date: 'February 5, 2025',
      status: 'upcoming',
      amount: 800,
      assetsUploaded: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to={`/photographer/1`}>
                <Button variant="ghost" size="sm" data-testid="view-profile">View My Profile</Button>
              </Link>
              <Button variant="ghost" size="icon" data-testid="profile-button">
                <img
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-2" data-testid="dashboard-title">
            Welcome Back, Sarah
          </h1>
          <p className="text-lg text-muted-foreground" data-testid="dashboard-subtitle">
            Manage your bookings, upload assets, and grow your business
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="stats-grid">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Total Earnings
                <DollarSign className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold mb-1" data-testid="stat-earnings">$42,580</div>
              <div className="text-xs text-green-600 flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" />
                +12% from last month
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Total Bookings
                <Calendar className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-bookings">28</div>
              <div className="text-xs text-muted-foreground">3 upcoming</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Average Rating
                <Star className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-rating">4.9</div>
              <div className="text-xs text-muted-foreground">From 127 reviews</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                Assets Delivered
                <ImageIcon className="w-4 h-4" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-assets">12,487</div>
              <div className="text-xs text-muted-foreground">All time</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Upcoming Bookings */}
            <section data-testid="bookings-section">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="bookings-title">My Bookings</h2>

              <div className="space-y-4">
                {bookings.map((booking) => (
                  <Card key={booking.id} data-testid={`booking-${booking.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-playfair text-xl font-semibold" data-testid={`booking-type-${booking.id}`}>
                              {booking.type}
                            </h3>
                            {booking.status === 'completed' ? (
                              <Badge className="bg-green-600" data-testid={`status-${booking.id}`}>Completed</Badge>
                            ) : (
                              <Badge variant="secondary" data-testid={`status-${booking.id}`}>Upcoming</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mb-2" data-testid={`client-${booking.id}`}>
                            Client: {booking.client}
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span data-testid={`date-${booking.id}`}>{booking.date}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-playfair text-2xl font-bold" data-testid={`amount-${booking.id}`}>
                            ${booking.amount.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-4 border-t">
                        {booking.assetsUploaded ? (
                          <Badge variant="outline" className="text-green-600 border-green-600" data-testid={`assets-status-${booking.id}`}>
                            Assets Delivered
                          </Badge>
                        ) : (
                          <Button size="sm" data-testid={`upload-assets-${booking.id}`}>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Assets
                          </Button>
                        )}
                        <Button variant="outline" size="sm" data-testid={`view-details-${booking.id}`}>
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Completion */}
            <Card data-testid="profile-completion">
              <CardHeader>
                <CardTitle>Profile Completion</CardTitle>
                <CardDescription>Complete your profile to get more bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2 text-sm">
                      <span>Progress</span>
                      <span className="font-semibold" data-testid="profile-progress">85%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: '85%' }} />
                    </div>
                  </div>
                  <div className="text-sm space-y-2">
                    <div className="flex items-center text-green-600">
                      <div className="w-4 h-4 rounded-full bg-green-600 flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      Portfolio uploaded
                    </div>
                    <div className="flex items-center text-green-600">
                      <div className="w-4 h-4 rounded-full bg-green-600 flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      Packages configured
                    </div>
                    <div className="flex items-center text-muted-foreground">
                      <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center mr-2" />
                      Add availability calendar
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full" data-testid="edit-profile">
                    Edit Profile
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card data-testid="quick-actions">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" data-testid="action-calendar">
                  <Calendar className="w-4 h-4 mr-2" />
                  Manage Availability
                </Button>
                <Button variant="outline" className="w-full justify-start" data-testid="action-pricing">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Update Pricing
                </Button>
                <Button variant="outline" className="w-full justify-start" data-testid="action-portfolio">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Edit Portfolio
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotographerDashboard;